import React, { useState, useMemo, useCallback } from 'react';
import ContextMenu from './ContextMenu';

const OrderBook = React.memo(({ bids, asks, onOrderClick, onCancelOrder, onModifyOrder, instrument, tickSize, validatePrice, roundToTick, lastTradedPrice, userId }) => {
  const [inlineForm, setInlineForm] = useState(null);
  const [depthRows, setDepthRows] = useState(5);
  const [contextMenu, setContextMenu] = useState({ visible: false, x: 0, y: 0, items: [] });
  
  
  // Memoize the formatted data to prevent unnecessary re-renders
  const formattedBids = useMemo(() => {
    const formatted = bids.map(order => ({
      ...order,
      formattedPrice: typeof order.price === 'number' ? order.price.toFixed(2) : '—',
      formattedQuantity: typeof (order.remaining_quantity ?? order.quantity ?? 0) === 'number' 
        ? (order.remaining_quantity ?? order.quantity ?? 0).toFixed(2) : '—'
    }));
    
    // Pad with empty rows to always show depthRows number of lines
    const emptyRows = Array(Math.max(0, depthRows - formatted.length)).fill(null).map((_, i) => ({
      order_id: `empty-bid-${i}`,
      isEmpty: true,
      formattedPrice: '—',
      formattedQuantity: '—'
    }));
    
    return [...formatted.slice(0, depthRows), ...emptyRows];
  }, [bids, depthRows]);

  const formattedAsks = useMemo(() => {
    const formatted = asks.map(order => ({
      ...order,
      formattedPrice: typeof order.price === 'number' ? order.price.toFixed(2) : '—',
      formattedQuantity: typeof (order.remaining_quantity ?? order.quantity ?? 0) === 'number' 
        ? (order.remaining_quantity ?? order.quantity ?? 0).toFixed(2) : '—'
    }));
    
    // Pad with empty rows to always show depthRows number of lines
    const emptyRows = Array(Math.max(0, depthRows - formatted.length)).fill(null).map((_, i) => ({
      order_id: `empty-ask-${i}`,
      isEmpty: true,
      formattedPrice: '—',
      formattedQuantity: '—'
    }));
    
    return [...formatted.slice(0, depthRows), ...emptyRows];
  }, [asks, depthRows]);

  const showInlineForm = useCallback((order, side, orderType = 'LIMIT') => {
    // For empty rows, use lastTradedPrice as reference and qty of 1
    const price = order.isEmpty ? (lastTradedPrice || 0) : order.price;
    const quantity = order.isEmpty ? 1 : (order.remaining_quantity || order.quantity || 1);
    
    setInlineForm({
      order,
      side,
      orderType,
      price: price,
      stopPrice: price,  // Pre-fill stop price from order
      limitPrice: price, // Pre-fill limit price from order
      quantity: quantity,
      tickSize: tickSize
    });
  }, [tickSize, lastTradedPrice]);

  const hideInlineForm = useCallback(() => {
    setInlineForm(null);
  }, []);

  const submitInlineOrder = useCallback(async (formData) => {
    // Generate client_order_id in YYYYMMDDHHMMSS format
    const now = new Date();
    const clientOrderId = now.getFullYear().toString() +
      (now.getMonth() + 1).toString().padStart(2, '0') +
      now.getDate().toString().padStart(2, '0') +
      now.getHours().toString().padStart(2, '0') +
      now.getMinutes().toString().padStart(2, '0') +
      now.getSeconds().toString().padStart(2, '0');

    const orderData = {
      user_id: formData.userId,
      side: formData.side === 'bid' ? 'BUY' : 'SELL',
      quantity: Number(formData.quantity),
      price: formData.orderType === 'MARKET' ? null : (formData.orderType === 'STOP_LIMIT' ? null : Number(formData.price)),
      order_type: formData.orderType || 'LIMIT',
      symbol: instrument,  // Use dynamic instrument
      client_order_id: clientOrderId  // Add client order ID
    };
    
    // Add stop_price and limit_price for STOP_LIMIT orders
    if (orderData.order_type === 'STOP_LIMIT') {
      orderData.stop_price = Number(formData.stopPrice);
      orderData.limit_price = Number(formData.limitPrice);
      
      // Validate tick size for stop_price and limit_price
      if (!validatePrice(instrument, orderData.stop_price)) {
        alert(`Invalid stop price ${orderData.stop_price}. Must be a multiple of tick size ${tickSize}`);
        return;
      }
      if (!validatePrice(instrument, orderData.limit_price)) {
        alert(`Invalid limit price ${orderData.limit_price}. Must be a multiple of tick size ${tickSize}`);
        return;
      }
    }
    
    // Validate tick size for limit orders
    if (orderData.order_type === 'LIMIT' && orderData.price) {
      if (!validatePrice(instrument, orderData.price)) {
        alert(`Invalid price ${orderData.price}. Must be a multiple of tick size ${tickSize}`);
        return;
      }
    }
    
    const result = await onOrderClick(orderData);
    if (result.success) {
      hideInlineForm();
    }
    return result;
  }, [onOrderClick, hideInlineForm, instrument, tickSize, validatePrice]);

  const handleRowClick = useCallback((e, order) => {
    if (!e.target.classList.contains('cancel-btn')) {
      showInlineForm(order, 'bid');
    }
  }, [showInlineForm]);

  const handleAskRowClick = useCallback((e, order) => {
    if (!e.target.classList.contains('cancel-btn')) {
      showInlineForm(order, 'ask');
    }
  }, [showInlineForm]);
  
  const handleEmptyRowClick = useCallback((e, order, side) => {
    // Use the existing empty order's order_id so the form can be matched
    const emptyOrder = {
      ...order,
      price: lastTradedPrice || 0,
      quantity: 1
    };
    showInlineForm(emptyOrder, side);
  }, [showInlineForm, lastTradedPrice]);

  const handleCancelClick = useCallback((e, orderId, orderUserId, symbol) => {
    e.stopPropagation();
    // Use order's user_id if available, otherwise fall back to component's userId prop
    // This ensures we always have a valid userId for cancellation
    const cancelUserId = orderUserId || userId || '';
    console.log('🔍 [CANCEL CLICK] Canceling order:', { orderId, orderUserId, cancelUserId, symbol });
    onCancelOrder(orderId, cancelUserId, symbol);
  }, [onCancelOrder, userId]);

  const handleRightClick = useCallback((e, order) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (order.isEmpty) return;
    
    const isUserOrder = order.user_id && order.user_id !== '';
    const oppositeSide = order.side === 'buy' ? 'sell' : 'buy';
    
    const contextMenuItems = [
      {
        label: `Place ${oppositeSide.toUpperCase()} Order at ${order.formattedPrice}`,
        icon: oppositeSide === 'buy' ? '📈' : '📉',
        action: () => {
          showInlineForm(order, oppositeSide);
        }
      },
      {
        label: `Place ${oppositeSide.toUpperCase()} Stop Limit at ${order.formattedPrice}`,
        icon: '🛑',
        action: () => {
          showInlineForm(order, oppositeSide, 'STOP_LIMIT');
        }
      },
      {
        label: 'Copy Price',
        icon: '📋',
        shortcut: 'Ctrl+C',
        action: () => {
          navigator.clipboard.writeText(order.price.toString());
        }
      },
      {
        label: 'Copy Quantity',
        icon: '📊',
        action: () => {
          const qty = order.remaining_quantity || order.quantity || 0;
          navigator.clipboard.writeText(qty.toString());
        }
      }
    ];
    
    // Add user-specific options
    if (isUserOrder) {
      contextMenuItems.push(
        {
          label: 'Modify Order',
          icon: '✏️',
          action: () => {
            if (onModifyOrder) {
              // Create a synthetic event for context menu with smart positioning
              const modalWidth = 400;
              const modalHeight = 300;
              const padding = 20;
              
              // Center the modal in the viewport
              let x = Math.max(padding, (window.innerWidth - modalWidth) / 2);
              let y = Math.max(padding, (window.innerHeight - modalHeight) / 2);
              
              const syntheticEvent = {
                target: {
                  getBoundingClientRect: () => ({
                    left: x,
                    top: y - modalHeight - 10,
                    bottom: y
                  })
                }
              };
              onModifyOrder(order, syntheticEvent);
            }
          }
        },
        {
          label: 'Cancel Order',
          icon: '❌',
          shortcut: 'Del',
          action: () => {
            onCancelOrder(order.order_id, order.user_id);
          }
        }
      );
    }
    
    setContextMenu({
      visible: true,
      x: e.clientX,
      y: e.clientY,
      items: contextMenuItems
    });
  }, [onCancelOrder, onModifyOrder, showInlineForm]);

  const closeContextMenu = useCallback(() => {
    setContextMenu({ visible: false, x: 0, y: 0, items: [] });
  }, []);

  return (
    <div className="order-book-grid">
      {/* Instrument Label */}
      <div 
        style={{gridColumn: '1 / -1', padding: '8px 12px', background: 'var(--bg)', border: '1px solid var(--border)', borderRadius: '8px', marginBottom: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center'}}
        title={`Instrument: ${instrument} | Tick Size: ${tickSize} | Min Price Increment: ${tickSize}`}
      >
        <div>
          <div style={{fontSize: '12px', fontWeight: '600', color: 'var(--muted)', marginBottom: '2px'}}>Instrument:</div>
          <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
            <div style={{fontSize: '14px', fontWeight: '500', color: 'var(--text)'}}>{instrument}</div>
            {lastTradedPrice && (
              <div style={{fontSize: '11px', color: 'var(--muted)'}}>
                Last: <span style={{color: 'var(--good)', fontWeight: '600'}}>{lastTradedPrice}</span>
              </div>
            )}
          </div>
        </div>
        <div style={{display: 'flex', alignItems: 'center', gap: '8px'}}>
          <label style={{fontSize: '12px', fontWeight: '600', color: 'var(--muted)'}}>Depth:</label>
          <input
            type="number"
            min="1"
            max="50"
            value={depthRows}
            onChange={(e) => setDepthRows(Math.max(1, Math.min(50, Number(e.target.value) || 5)))}
            style={{
              width: '60px',
              padding: '4px 8px',
              background: 'var(--panel)',
              border: '1px solid var(--border)',
              borderRadius: '6px',
              color: 'var(--text)',
              fontSize: '13px',
              textAlign: 'center'
            }}
          />
        </div>
      </div>
      
      {/* Bids (Buy) */}
      <div className="order-book-side">
        <div className="order-book-header">Bids (Buy)</div>
        <div className="order-book-header" style={{gridTemplateColumns: '80px 1fr 1fr 1fr', display: 'grid', gap: '8px', padding: '8px 12px', fontSize: '14px', color: 'var(--muted)', fontWeight: '600', borderBottom: '1px solid var(--border)'}}>
          <div>Cancel</div>
          <div>User</div>
          <div>QTY</div>
          <div>Price</div>
        </div>
        <div style={{padding: '0 12px'}}>
          {formattedBids.map((order) => (
            <div key={order.order_id} style={{position: 'relative'}}>
              <div
                className={order.isEmpty ? "order-book-row bid" : "order-book-row bid clickable"}
                onClick={order.isEmpty ? (e) => handleEmptyRowClick(e, order, 'bid') : (e) => handleRowClick(e, order)}
                onContextMenu={order.isEmpty ? undefined : (e) => handleRightClick(e, order)}
                style={{gridTemplateColumns: '80px 1fr 1fr 1fr', display: 'grid', gap: '8px', padding: '4px 0', borderBottom: '1px dashed rgba(255,255,255,.03)', alignItems: 'center', fontSize: '14px', cursor: 'pointer', transition: 'background-color 0.2s ease', opacity: order.isEmpty ? 0.3 : 1}}
              >
                {!order.isEmpty ? (
                  <button
                    className="cancel-btn"
                    onClick={(e) => handleCancelClick(e, order.order_id, order.user_id, order.symbol || instrument)}
                    disabled={!order.user_id}
                    style={{background: 'transparent', border: '1px solid var(--border)', padding: '2px 6px', borderRadius: '6px', color: 'var(--muted)', cursor: 'pointer', fontSize: '13px', textAlign: 'center'}}
                  >
                    Cancel
                  </button>
                ) : (
                  <div style={{width: '80px'}}></div>
                )}
                <div className="user" style={{textAlign: 'center', fontSize: '14px', color: 'var(--muted)'}}>
                  {order.isEmpty ? '—' : (order.user_id ? String(order.user_id) : '—')}
                </div>
                <div className="quantity" style={{fontVariantNumeric: 'tabular-nums', textAlign: 'right'}}>
                  {order.formattedQuantity}
                </div>
                <div className="price" style={{fontVariantNumeric: 'tabular-nums', textAlign: 'right', color: 'var(--good)'}}>
                  {order.formattedPrice}
                </div>
              </div>
              
              {/* Inline form appears below the clicked row */}
              {inlineForm && inlineForm.order.order_id === order.order_id && (
                <div className="inline-order-form" style={{margin: '4px 0', padding: '8px', background: 'var(--panel)', border: '1px solid var(--accent)', borderRadius: '6px'}}>
                  <h4 style={{margin: '0 0 6px 0', fontSize: '12px', color: 'var(--accent)'}}>
                    {inlineForm.side === 'bid' ? 'BUY' : 'SELL'} Order
                  </h4>
                  <InlineOrderForm
                    initialData={inlineForm}
                    onSubmit={submitInlineOrder}
                    onCancel={hideInlineForm}
                    userId={userId}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>

      {/* Asks (Sell) */}
      <div className="order-book-side">
        <div className="order-book-header">Asks (Sell)</div>
        <div className="order-book-header" style={{gridTemplateColumns: '1fr 1fr 1fr 80px', display: 'grid', gap: '8px', padding: '8px 12px', fontSize: '14px', color: 'var(--muted)', fontWeight: '600', borderBottom: '1px solid var(--border)'}}>
          <div>Price</div>
          <div>QTY</div>
          <div>User</div>
          <div>Cancel</div>
        </div>
        <div style={{padding: '0 12px'}}>
          {formattedAsks.map((order) => (
            <div key={order.order_id} style={{position: 'relative'}}>
              <div
                className={order.isEmpty ? "order-book-row ask" : "order-book-row ask clickable"}
                onClick={order.isEmpty ? (e) => handleEmptyRowClick(e, order, 'ask') : (e) => handleAskRowClick(e, order)}
                onContextMenu={order.isEmpty ? undefined : (e) => handleRightClick(e, order)}
                style={{gridTemplateColumns: '1fr 1fr 1fr 80px', display: 'grid', gap: '8px', padding: '4px 0', borderBottom: '1px dashed rgba(255,255,255,.03)', alignItems: 'center', fontSize: '14px', cursor: 'pointer', transition: 'background-color 0.2s ease', opacity: order.isEmpty ? 0.3 : 1}}
              >
                <div className="price" style={{fontVariantNumeric: 'tabular-nums', textAlign: 'right', color: 'var(--bad)'}}>
                  {order.formattedPrice}
                </div>
                <div className="quantity" style={{fontVariantNumeric: 'tabular-nums', textAlign: 'right'}}>
                  {order.formattedQuantity}
                </div>
                <div className="user" style={{textAlign: 'center', fontSize: '14px', color: 'var(--muted)'}}>
                  {order.isEmpty ? '—' : (order.user_id ? String(order.user_id) : '—')}
                </div>
                {!order.isEmpty ? (
                  <button
                    className="cancel-btn"
                    onClick={(e) => handleCancelClick(e, order.order_id, order.user_id, order.symbol || instrument)}
                    disabled={!order.user_id}
                    style={{background: 'transparent', border: '1px solid var(--border)', padding: '2px 6px', borderRadius: '6px', color: 'var(--muted)', cursor: 'pointer', fontSize: '13px', textAlign: 'center'}}
                  >
                    Cancel
                  </button>
                ) : (
                  <div style={{width: '80px'}}></div>
                )}
              </div>
              
              {/* Inline form appears below the clicked row */}
              {inlineForm && inlineForm.order.order_id === order.order_id && (
                <div className="inline-order-form" style={{margin: '4px 0', padding: '8px', background: 'var(--panel)', border: '1px solid var(--accent)', borderRadius: '6px'}}>
                  <h4 style={{margin: '0 0 6px 0', fontSize: '12px', color: 'var(--accent)'}}>
                    {inlineForm.side === 'bid' ? 'BUY' : 'SELL'} Order
                  </h4>
                  <InlineOrderForm
                    initialData={inlineForm}
                    onSubmit={submitInlineOrder}
                    onCancel={hideInlineForm}
                    userId={userId}
                  />
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
      
      {/* Context Menu */}
      <ContextMenu
        x={contextMenu.x}
        y={contextMenu.y}
        items={contextMenu.items}
        visible={contextMenu.visible}
        onClose={closeContextMenu}
      />
    </div>
  );
});

OrderBook.displayName = 'OrderBook';

const InlineOrderForm = React.memo(({ initialData, onSubmit, onCancel, userId }) => {
  const [orderType, setOrderType] = useState(initialData.orderType || 'LIMIT');
  const [price, setPrice] = useState(initialData.price != null ? String(initialData.price) : '');
  const [stopPrice, setStopPrice] = useState(initialData.stopPrice != null ? String(initialData.stopPrice) : (initialData.price != null ? String(initialData.price) : ''));
  const [limitPrice, setLimitPrice] = useState(initialData.limitPrice != null ? String(initialData.limitPrice) : (initialData.price != null ? String(initialData.price) : ''));
  const [quantity, setQuantity] = useState(initialData.quantity != null ? String(initialData.quantity) : '');
  const [status, setStatus] = useState('');
  
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Validation
    if (!userId || !quantity) {
      setStatus('❌ Please fill in User ID and Quantity');
      return;
    }

    if (orderType === 'LIMIT' && (!price || price === '' || isNaN(Number(price)) || Number(price) <= 0)) {
      setStatus('❌ Please enter a valid price for limit orders');
      return;
    }

    if (orderType === 'STOP_LIMIT') {
      if (!stopPrice || stopPrice === '' || isNaN(Number(stopPrice)) || Number(stopPrice) <= 0) {
        setStatus('❌ Please enter a valid stop price for stop limit orders');
        return;
      }
      if (!limitPrice || limitPrice === '' || isNaN(Number(limitPrice)) || Number(limitPrice) <= 0) {
        setStatus('❌ Please enter a valid limit price for stop limit orders');
        return;
      }
    }
    
    setStatus('⏳ Submitting order...');
    
    const submitData = {
      side: initialData.side,
      orderType,
      price: orderType === 'LIMIT' ? Number(price) : null,
      quantity: Number(quantity),
      userId: userId // Use shared userId from props
    };
    
    // Add stop_price and limit_price for STOP_LIMIT orders
    if (orderType === 'STOP_LIMIT') {
      submitData.stopPrice = Number(stopPrice);
      submitData.limitPrice = Number(limitPrice);
    }
    
    const result = await onSubmit(submitData);
    
    if (result.success) {
      setStatus('✅ Order submitted successfully');
    } else {
      setStatus(`❌ Order failed: ${result.message}`);
    }
  };
  
  return (
    <form onSubmit={handleSubmit}>
      <div className="order-type-section">
        <label style={{display: 'block', marginBottom: '4px', fontSize: '11px', fontWeight: '500', color: 'var(--muted)'}}>Order Type:</label>
        <select 
          value={orderType} 
          onChange={(e) => setOrderType(e.target.value)}
          style={{width: '100%', padding: '6px 8px', fontSize: '12px', background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: '4px'}}
        >
          <option value="LIMIT">Limit Order</option>
          <option value="MARKET">Market Order</option>
          <option value="STOP_LIMIT">Stop Limit Order</option>
        </select>
      </div>
      
      {orderType === 'LIMIT' && (
        <div className="price-section" style={{marginTop: '6px'}}>
          <label style={{display: 'block', marginBottom: '4px', fontSize: '11px', fontWeight: '500', color: 'var(--muted)'}}>Price:</label>
          <input
            type="number"
            value={price}
            onChange={(e) => setPrice(e.target.value)}
            step={initialData.tickSize || 0.01}
            min="0"
            required
            style={{width: '100%', padding: '6px 8px', fontSize: '12px', background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: '4px'}}
          />
        </div>
      )}
      
      {orderType === 'STOP_LIMIT' && (
        <>
          <div className="stop-price-section" style={{marginTop: '6px'}}>
            <label style={{display: 'block', marginBottom: '4px', fontSize: '11px', fontWeight: '500', color: 'var(--muted)'}}>Stop Price:</label>
            <input
              type="number"
              value={stopPrice}
              onChange={(e) => setStopPrice(e.target.value)}
              step={initialData.tickSize || 0.01}
              min="0"
              required
              style={{width: '100%', padding: '6px 8px', fontSize: '12px', background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: '4px'}}
              title="Trigger price - order activates when price reaches this level"
            />
          </div>
          <div className="limit-price-section" style={{marginTop: '6px'}}>
            <label style={{display: 'block', marginBottom: '4px', fontSize: '11px', fontWeight: '500', color: 'var(--muted)'}}>Limit Price:</label>
            <input
              type="number"
              value={limitPrice}
              onChange={(e) => setLimitPrice(e.target.value)}
              step={initialData.tickSize || 0.01}
              min="0"
              required
              style={{width: '100%', padding: '6px 8px', fontSize: '12px', background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: '4px'}}
              title="Limit price - order executes at this price or better after trigger"
            />
          </div>
        </>
      )}
      
      <div className="form-group" style={{marginTop: '6px'}}>
        <label style={{display: 'block', marginBottom: '4px', fontSize: '11px', fontWeight: '500', color: 'var(--muted)'}}>Quantity:</label>
        <input
          type="number"
          value={quantity}
          onChange={(e) => setQuantity(e.target.value)}
          min="1"
          required
          style={{width: '100%', padding: '6px 8px', fontSize: '12px', background: 'var(--panel)', border: '1px solid var(--border)', color: 'var(--text)', borderRadius: '4px'}}
        />
      </div>
      
      <div className="btn-group" style={{display: 'flex', gap: '6px', marginTop: '6px'}}>
        <button type="submit" className="btn" style={{background: 'var(--accent)', border: 'none', color: 'white', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px'}}>
          Submit {orderType} Order
        </button>
        <button type="button" className="btn cancel" onClick={onCancel} style={{background: 'transparent', border: '1px solid var(--border)', color: 'var(--muted)', padding: '5px 10px', borderRadius: '4px', cursor: 'pointer', fontSize: '11px'}}>
          Cancel
        </button>
      </div>
      
      {status && (
        <div className={`status ${status.includes('✅') ? 'ok' : status.includes('❌') ? 'error' : 'warn'}`} style={{padding: '10px 12px', borderTop: '1px solid var(--border)', display: 'flex', gap: '8px', alignItems: 'center', fontSize: '13px', marginTop: '8px'}}>
          {status}
        </div>
      )}
    </form>
  );
});

InlineOrderForm.displayName = 'InlineOrderForm';

export default OrderBook;